import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { formatCurrency, formatPrice } from "@/lib/format";
import { TradeActions, TradeQuantity, TradeTargets } from "@/components/trade/TradeControls";
import { defaultTargets, useTradeOrder } from "@/components/trade/useTradeOrder";
import { useLanguage } from "@/context/LanguageContext";

const PriceBox = ({ label, price, color, testId }) => (
  <div className="rounded border border-[#1D2637] bg-[#0B111C] px-3 py-2">
    <p className="text-[9px] uppercase tracking-[0.2em] text-[#8A97AD]">{label}</p>
    <p className="font-mono text-sm" style={{ color }} data-testid={testId}>{formatPrice(price)}</p>
  </div>
);

export const TradePanel = ({ asset, open, onOpenChange, onDone, freeMargin, leverage = 30 }) => {
  const { t } = useLanguage();
  const [quantity, setQuantity] = useState("1");
  const [advanced, setAdvanced] = useState(false);
  const [tp, setTp] = useState("");
  const [sl, setSl] = useState("");
  const [touched, setTouched] = useState({ tp: false, sl: false });

  const qty = Number(quantity) || 0;
  const marginCost = (qty * (asset?.buy || 0)) / (leverage || 1);
  const pctStep = asset?.buy ? +(asset.buy * 0.02).toFixed(6) : 0.0001;

  const { busy, submit } = useTradeOrder({
    asset,
    advanced,
    tp,
    sl,
    touched,
    onSuccess: () => {
      onOpenChange(false);
      setTp("");
      setSl("");
      setTouched({ tp: false, sl: false });
      onDone?.();
    },
  });

  const toggleAdvanced = () => {
    const next = !advanced;
    setAdvanced(next);
    if (next && asset?.buy) {
      const auto = defaultTargets("buy", asset.buy);
      if (!tp) setTp(String(auto.tp));
      if (!sl) setSl(String(auto.sl));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[340px] gap-0 border-[#1D2637] bg-[#0A0E16] p-0 text-white"
        data-testid="trade-panel"
      >
        <DialogHeader className="border-b border-[#141A26] px-4 py-3">
          <DialogTitle className="flex items-center justify-between font-mono text-xs font-normal tracking-[0.1em] text-white">
            <span>{asset?.symbol}</span>
            <span className="text-[10px] text-[#8A97AD]">{t("trade.marketOrder")}</span>
          </DialogTitle>
          <DialogDescription className="sr-only">
            {t("trade.marketOrder")} — {asset?.symbol}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 p-4">
          <div className="grid grid-cols-2 gap-2">
            <PriceBox label="Sell" price={asset?.sell} color="#EF4444" testId="trade-sell-price" />
            <PriceBox label="Buy" price={asset?.buy} color="#10B981" testId="trade-buy-price" />
          </div>

          <TradeQuantity label={t("trade.volume")} quantity={quantity}
                         onChange={setQuantity}
                         onStep={(d) => setQuantity(String(Math.max(0, +(qty + d).toFixed(6))))} />

          <button
            onClick={toggleAdvanced}
            data-testid="trade-advanced-toggle"
            className="flex w-full items-center justify-between rounded border border-[#1D2637] px-3 py-2 text-[10px] uppercase tracking-[0.2em] text-[#8A97AD] transition-colors hover:text-white"
          >
            {t("trade.advanced")}
            <ChevronDown className={`h-3 w-3 transition-transform ${advanced ? "rotate-180" : ""}`} />
          </button>

          {advanced ? (
            <TradeTargets
              labels={{ tp: t("trade.tp"), sl: t("trade.sl") }}
              tp={tp}
              sl={sl}
              step={pctStep}
              onTp={(v) => { setTouched((s) => ({ ...s, tp: true })); setTp(v); }}
              onSl={(v) => { setTouched((s) => ({ ...s, sl: true })); setSl(v); }}
            />
          ) : null}

          <div className="flex items-center justify-between border-t border-[#141A26] pt-3 font-mono text-[10px] text-[#8A97AD]">
            <span data-testid="trade-estimated-cost">{t("trade.margin")} ≈ {formatCurrency(marginCost)}</span>
            <span>{t("trade.free")} {formatCurrency(freeMargin || 0)}</span>
          </div>

          <TradeActions asset={asset} busy={busy} disabled={!!busy || qty <= 0}
                        onSubmit={(side) => submit(side, quantity)} />
        </div>
      </DialogContent>
    </Dialog>
  );
};
